import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../../../state'
import * as shared from '../../../../../shared'

/** Identifies this report to the configuration plugin. */
const REPORT_ID = ents.extra.space.Report.Id.DataExport

/** Page content for the Data Export space report. */
@ngCore.Component({
  imports: [
    ds.input.select.multiple.Component,
    ds.input.select.single.Component,
    ds.layout.section.Component,
    ds.layout.widget.Component,
    shared.dimensionFilter.Component,
  ],
  selector: 'atn-space-report-data-export',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.space.reports.Collection} */
  private readonly _reportsEc = ngCore.inject(state.space.reports.Collection)
  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /** @see {@link state.space.reports.plugins.configuration.Plugin} */
  public readonly configurationPlugin = this._reportsEc.plugins
    .lookup(state.space.reports.plugins.configuration.Plugin)
    .get()

  /** @see {@link state.space.reports.plugins.organizationDimensionFilter.Plugin} */
  public readonly organizationFilterPlugin = this._reportsEc.plugins
    .lookup(state.space.reports.plugins.organizationDimensionFilter.Plugin)
    .get()

  /** @see {@link state.space.reports.plugins.spaceDimensionFilter.Plugin} */
  public readonly spaceFilterPlugin = this._reportsEc.plugins
    .lookup(state.space.reports.plugins.spaceDimensionFilter.Plugin)
    .get()

  /** @see {@link state.space.reports.plugins.clusterDimensionFilter.Plugin} */
  public readonly clusterFilterPlugin = this._reportsEc.plugins
    .lookup(state.space.reports.plugins.clusterDimensionFilter.Plugin)
    .get()

  /** Configuration settings for this report */
  public readonly configurationSg =
    this.configurationPlugin.selectors.getSettings(REPORT_ID).asSignal

  /**
   * Options for the fields select, in the order of the report's columns,
   * leaving out the levels the setup doesn't deploy
   */
  public readonly fieldItemsSg = ngCore.computed(
    (
      schema = this._schemaFeature.selectors.schema.asSignal(),
    ): readonly ds.input.select.models.Item<ents.extra.space.Report.Configuration.DataExport.Field>[] =>
      ents.extra.space.Report.Configuration.DataExport.Field.listFor(
        schema,
      ).map(field => ({
        label:
          ents.extra.space.Report.Configuration.DataExport.Field.getLabel(
            field,
          ),
        value: field,
      })),
  )

  /**
   * Options for the occupant fields select, in the order of the report's
   * columns
   */
  public readonly occupantFieldItems: readonly ds.input.select.models.Item<ents.extra.space.Report.Configuration.DataExport.OccupantField>[] =
    ents.extra.space.Report.Configuration.DataExport.OccupantField.ALL.map(
      field => ({
        label:
          ents.extra.space.Report.Configuration.DataExport.OccupantField.getLabel(
            field,
          ),
        value: field,
      }),
    )

  /**
   * Options for the account fields select, in the order of the report's
   * columns
   */
  public readonly accountFieldItems: readonly ds.input.select.models.Item<ents.extra.space.Report.Configuration.DataExport.AccountField>[] =
    ents.extra.space.Report.Configuration.DataExport.AccountField.ALL.map(
      field => ({
        label:
          ents.extra.space.Report.Configuration.DataExport.AccountField.getLabel(
            field,
          ),
        value: field,
      }),
    )

  /**
   * Options for the responsible person fields select, in the order of the
   * report's columns
   */
  public readonly responsiblePersonFieldItems: readonly ds.input.select.models.Item<ents.extra.space.Report.Configuration.DataExport.ResponsiblePersonField>[] =
    ents.extra.space.Report.Configuration.DataExport.ResponsiblePersonField.ALL.map(
      field => ({
        label:
          ents.extra.space.Report.Configuration.DataExport.ResponsiblePersonField.getLabel(
            field,
          ),
        value: field,
      }),
    )

  /**
   * Options for the functional usage fields select, in the order of the
   * report's columns
   */
  public readonly functionalUsageFieldItems: readonly ds.input.select.models.Item<ents.extra.space.Report.Configuration.DataExport.FunctionalUsageField>[] =
    ents.extra.space.Report.Configuration.DataExport.FunctionalUsageField.ALL.map(
      field => ({
        label:
          ents.extra.space.Report.Configuration.DataExport.FunctionalUsageField.getLabel(
            field,
          ),
        value: field,
      }),
    )

  /** Options for the groups' layout selects */
  public readonly layoutItems: readonly ds.input.select.models.Item<ents.extra.space.Report.Configuration.DataExport.Layout>[] =
    ents.extra.space.Report.Configuration.DataExport.Layout.ALL.map(layout => ({
      label:
        ents.extra.space.Report.Configuration.DataExport.Layout.getLabel(
          layout,
        ),
      value: layout,
    }))

  /** Handles a change to the fields select */
  public fieldsChangeHandler(
    values: readonly (
      | ents.extra.space.Report.Configuration.DataExport.Field
      | undefined
    )[],
  ): void {
    this.configurationPlugin.actions.edit
      .from('SpaceReport.DataExport.Configuration.Fields.Changed')
      .dispatch({
        edit: { fields: values.filter(TypeChecker.isNotNullish.raw) },
        id: REPORT_ID,
      })
  }

  /** Handles a change to the occupant fields select */
  public occupantFieldsChangeHandler(
    values: readonly (
      | ents.extra.space.Report.Configuration.DataExport.OccupantField
      | undefined
    )[],
  ): void {
    this.configurationPlugin.actions.edit
      .from('SpaceReport.DataExport.Configuration.OccupantFields.Changed')
      .dispatch({
        edit: { occupantFields: values.filter(TypeChecker.isNotNullish.raw) },
        id: REPORT_ID,
      })
  }

  /** Handles a change to the account fields select */
  public accountFieldsChangeHandler(
    values: readonly (
      | ents.extra.space.Report.Configuration.DataExport.AccountField
      | undefined
    )[],
  ): void {
    this.configurationPlugin.actions.edit
      .from('SpaceReport.DataExport.Configuration.AccountFields.Changed')
      .dispatch({
        edit: { accountFields: values.filter(TypeChecker.isNotNullish.raw) },
        id: REPORT_ID,
      })
  }

  /** Handles a change to the responsible person fields select */
  public responsiblePersonFieldsChangeHandler(
    values: readonly (
      | ents.extra.space.Report.Configuration.DataExport.ResponsiblePersonField
      | undefined
    )[],
  ): void {
    this.configurationPlugin.actions.edit
      .from(
        'SpaceReport.DataExport.Configuration.ResponsiblePersonFields.Changed',
      )
      .dispatch({
        edit: {
          responsiblePersonFields: values.filter(TypeChecker.isNotNullish.raw),
        },
        id: REPORT_ID,
      })
  }

  /** Handles a change to the functional usage fields select */
  public functionalUsageFieldsChangeHandler(
    values: readonly (
      | ents.extra.space.Report.Configuration.DataExport.FunctionalUsageField
      | undefined
    )[],
  ): void {
    this.configurationPlugin.actions.edit
      .from(
        'SpaceReport.DataExport.Configuration.FunctionalUsageFields.Changed',
      )
      .dispatch({
        edit: {
          functionalUsageFields: values.filter(TypeChecker.isNotNullish.raw),
        },
        id: REPORT_ID,
      })
  }

  /**
   * Handles a change to a group's layout select, given the layout it changes
   * (e.g. `{ occupantLayout }`)
   *
   * NOTE: a select is only enabled, and then required, while its layout applies
   * to the picked fields, so it never clears the layout
   */
  public layoutChangeHandler(
    edit: Partial<
      Pick<
        ents.extra.space.Report.Configuration.DataExport,
        | 'accountLayout'
        | 'occupantLayout'
        | 'responsiblePersonLayout'
        | 'functionalUsageLayout'
      >
    >,
  ): void {
    this.configurationPlugin.actions.edit
      .from('SpaceReport.DataExport.Configuration.Layout.Changed')
      .dispatch({ edit, id: REPORT_ID })
  }
}
